import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { usePlayerProfile } from '../../contexts/PlayerProfileContext';
import { getRosterForDate, getCurrentDate, ACTIVE_LINEUP_REQUIREMENTS, getPitchers, getOutfielders, getUtility } from '../../utils/rosterManager';
import fantasyDataService from '../../services/fantasyDataService';
import { mapStatsFromAPI } from '../../services/fantasyDataService';
import { fetchGamesForDate, LIVE_GAMES_ENABLED } from '../../services/liveGamesService';
import { getAllStarBreakWeek, getMatchupDateRange, getMatchupDateBounds } from '../../utils/scheduleUtils';
import PlayerSilhouette from '../../components/PlayerSilhouette';
import TeamLogo from '../../components/TeamLogo';
import { buildFallbackMlbGames, getPlayerGameStatus } from '../../services/mlbGameStatus';
import { useTestDataBanner } from '../../contexts/TestDataBannerContext';

// The sample "games today" slate and the per-player in-game status now live in
// the shared src/services/mlbGameStatus.js, so the Roster page shows the same
// game state as this page. buildFallbackMlbGames() is the fallback used only if
// the live fetch fails, so the "MLB Scores" rail never renders blank; games 1 & 3
// carry a rich `Detail` object (at-bat, play-by-play, line/box score) that the
// sidebar Live/Box tabs render.

// ---- MLB game sidebar helpers (module scope; deterministic, no state) ----

const formatGameTime = (dateTimeStr) => {
  if (!dateTimeStr) return 'TBD';
  const d = new Date(dateTimeStr);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

const inningOrdinal = (n) => {
  if (n == null || n === '') return '';
  const d = Number(n);
  const v = d % 100;
  const suffix = (v >= 11 && v <= 13) ? 'th' : (d % 10 === 1 ? 'st' : d % 10 === 2 ? 'nd' : d % 10 === 3 ? 'rd' : 'th');
  return `${d}${suffix}`;
};

const isGameLive = (game) => game.Status === 'InProgress';
const isGameFinal = (game) => game.Status === 'Final' || game.Status === 'F/OT';

// Mini bases diamond (2B on top, 1B right, 3B left) from SportsData-style flags.
function MgsBases({ game }) {
  return (
    <div className="mgs-bases" aria-hidden="true">
      <span className={`mgs-b b2${game.RunnerOn2B ? ' on' : ''}`} />
      <span className={`mgs-b b1${game.RunnerOn1B ? ' on' : ''}`} />
      <span className={`mgs-b b3${game.RunnerOn3B ? ' on' : ''}`} />
    </div>
  );
}

function MgsOuts({ outs }) {
  const n = Number(outs) || 0;
  return (
    <div className="mgs-outs" aria-label={`${n} out${n === 1 ? '' : 's'}`}>
      {[0, 1, 2].map(i => <span key={i} className={`mgs-o${i < n ? ' on' : ''}`} />)}
    </div>
  );
}

// Renders the matchup table's "Game" cell for one player: opponent + score, the
// live inning / bases / outs situation, and their batting-order status (due up).
// `status` comes from getPlayerGameStatus(player, mlbGames).
function PlayerGameStatus({ status }) {
  if (!status) return <span className="pgs-none">—</span>;
  if (status.kind === 'pre') {
    return (
      <div className="pgs">
        <div className="pgs-opp">{status.opponent}</div>
        <div className="pgs-sub">{status.timeText}</div>
      </div>
    );
  }
  if (status.kind === 'final') {
    return (
      <div className="pgs">
        <div className="pgs-opp">{status.opponent} <span className="pgs-score">{status.score}</span></div>
        <div className="pgs-sub">{status.finalText}</div>
      </div>
    );
  }
  return (
    <div className="pgs">
      <div className="pgs-opp">{status.opponent} <span className="pgs-score">{status.score}</span></div>
      <div className="pgs-line">
        <span className="pgs-inn">{status.inningText}</span>
        <MgsBases game={status.game} />
        <MgsOuts outs={status.outs} />
      </div>
      {status.due && <div className={`pgs-due pgs-due-${status.dueClass}`}>{status.due}</div>}
    </div>
  );
}

// Live tab: current at-bat (strike zone + pitch log) and play-by-play.
function MgsLiveTab({ game }) {
  const atBat = game.Detail?.AtBat;
  const pbp = game.Detail?.PlayByPlay;
  if (!atBat && !(pbp && pbp.length)) {
    return (
      <div className="mgs-sec">
        <div className="mgs-empty">
          {isGameLive(game) ? 'Live detail is not available for this game.' : 'No live data for this game.'}
        </div>
      </div>
    );
  }
  return (
    <>
      {atBat && (
        <div className="mgs-sec">
          <div className="mgs-sec-h">At-Bat · Strike Zone</div>
          <div className="mgs-atbat">
            <div className="mgs-zone" aria-hidden="true">
              <div className="mgs-zbox" />
              <div className="mgs-plate" />
              {(atBat.Pitches || []).map(p => (
                <span key={p.n} className={`mgs-pitch ${p.result}`} style={{ left: `${p.x}%`, top: `${p.y}%` }}>{p.n}</span>
              ))}
            </div>
            <div className="mgs-ab-side">
              <div className="mgs-ab-vs">
                P: <b>{atBat.Pitcher?.name}</b> <span className="mgs-dim">({atBat.Pitcher?.hand})</span><br />
                AB: <b>{atBat.Batter?.name}</b> <span className="mgs-dim">({atBat.Batter?.hand})</span>
              </div>
              <div className="mgs-ab-count"><span className="c">{atBat.Balls}-{atBat.Strikes}</span><span className="cl">Count</span></div>
              <div className="mgs-plog">
                {(atBat.Pitches || []).map(p => (
                  <div key={p.n} className="mgs-plog-i"><span className={`mgs-plog-n ${p.result}`}>{p.n}</span> {p.desc}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      {pbp && pbp.length > 0 && (
        <div className="mgs-sec">
          <div className="mgs-sec-h">Play-by-Play</div>
          {pbp.map((seg, i) => (
            <React.Fragment key={`${seg.half}-${seg.inning}-${i}`}>
              <div className="mgs-inn-head">
                <span className="mgs-inn-side">{seg.half === 'B' ? '▼' : '▲'} {inningOrdinal(seg.inning)}</span>
                <span className="mgs-inn-team">{seg.label}</span>
                <span className="mgs-inn-score">{seg.score}</span>
              </div>
              <div className="mgs-pbp">
                {(seg.plays || []).map((p, j) => (
                  <div key={j} className="mgs-pbp-i">
                    <span className={`mgs-pbp-ico${p.kind ? ` ${p.kind}` : ''}`}>{p.icon}</span>
                    <div className="mgs-pbp-t">
                      {p.bold && <b>{p.bold}</b>}{p.bold ? ' ' : ''}{p.text}
                      {p.sub && <span className="mgs-pbp-sub"> · {p.sub}</span>}
                    </div>
                    {p.run && <span className="mgs-run-tag">{p.run}</span>}
                  </div>
                ))}
              </div>
              {seg.summary && <div className="mgs-inn-sum">{seg.summary}</div>}
            </React.Fragment>
          ))}
        </div>
      )}
    </>
  );
}

// Box tab: line score, per-team batting/pitching box tables, game info notes.
function MgsBoxTab({ game, side, onSideChange }) {
  const ls = game.Detail?.LineScore;
  const box = game.Detail?.Box;
  const info = game.Detail?.GameInfo;
  if (!ls && !box && !(info && info.length)) {
    return (
      <div className="mgs-sec">
        <div className="mgs-empty">Box score is not yet available for this game.</div>
      </div>
    );
  }
  const teamBox = box ? box[side] : null;
  return (
    <>
      {ls && (
        <div className="mgs-sec">
          <div className="mgs-ls-scroll">
            <table className="mgs-ls">
              <thead>
                <tr>
                  <th className="tm" />
                  {(ls.Away?.runs || []).map((_, i) => (
                    <th key={i} className={i + 1 === ls.CurrentInning ? 'cur' : undefined}>{i + 1}</th>
                  ))}
                  <th className="sep">R</th><th>H</th><th>E</th>
                </tr>
              </thead>
              <tbody>
                {[[game.AwayTeam, ls.Away], [game.HomeTeam, ls.Home]].map(([abbr, row]) => (
                  <tr key={abbr}>
                    <td className="tm">{abbr}</td>
                    {(row?.runs || []).map((r, i) => (
                      <td key={i} className={i + 1 === ls.CurrentInning ? 'cur' : undefined}>{r == null ? '·' : r}</td>
                    ))}
                    <td className="rhe r sep">{row?.R}</td>
                    <td className="rhe">{row?.H}</td>
                    <td className="rhe">{row?.E}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {box && (
        <div className="mgs-sec">
          <div className="mgs-ttog">
            <button type="button" className={side === 'away' ? 'on' : undefined} onClick={() => onSideChange('away')}>{game.AwayTeam}</button>
            <button type="button" className={side === 'home' ? 'on' : undefined} onClick={() => onSideChange('home')}>{game.HomeTeam}</button>
          </div>
          {teamBox ? (
            <>
              <div className="mgs-bx-sub">Batting</div>
              <div className="mgs-bx-scroll">
                <table className="mgs-bx">
                  <thead>
                    <tr><th className="nm">Batter</th><th>AB</th><th>R</th><th>H</th><th>RBI</th><th>BB</th><th>K</th><th>AVG</th><th>OPS</th></tr>
                  </thead>
                  <tbody>
                    {(teamBox.batting || []).map((b, i) => (
                      <tr key={i}>
                        <td className="nm">{b.name} <span className="pos">{b.pos}</span></td>
                        <td>{b.ab}</td><td>{b.r}</td><td className="k">{b.h}</td><td>{b.rbi}</td><td>{b.bb}</td><td>{b.k}</td><td>{b.avg}</td><td>{b.ops}</td>
                      </tr>
                    ))}
                    {teamBox.battingTotals && (
                      <tr className="tot">
                        <td className="nm">Totals</td>
                        <td>{teamBox.battingTotals.ab}</td><td>{teamBox.battingTotals.r}</td><td className="k">{teamBox.battingTotals.h}</td>
                        <td>{teamBox.battingTotals.rbi}</td><td>{teamBox.battingTotals.bb}</td><td>{teamBox.battingTotals.k}</td><td /><td />
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {(teamBox.notes || []).map((grp, i) => (
                <React.Fragment key={i}>
                  <div className="mgs-grp-h">{grp.group}</div>
                  {(grp.items || []).map((n, j) => (
                    <div key={j} className="mgs-note"><b>{n.b}</b> {n.t}</div>
                  ))}
                </React.Fragment>
              ))}
              <div className="mgs-bx-sub gap">Pitching</div>
              <div className="mgs-bx-scroll">
                <table className="mgs-bx">
                  <thead>
                    <tr><th className="nm">Pitcher</th><th>IP</th><th>H</th><th>R</th><th>ER</th><th>BB</th><th>K</th><th>HR</th><th>ERA</th></tr>
                  </thead>
                  <tbody>
                    {(teamBox.pitching || []).map((p, i) => (
                      <tr key={i}>
                        <td className="nm">{p.name}{p.note ? <span className="pos"> {p.note}</span> : null}</td>
                        <td>{p.ip}</td><td>{p.h}</td><td>{p.r}</td><td>{p.er}</td><td>{p.bb}</td><td className="k">{p.k}</td><td>{p.hr}</td><td>{p.era}</td>
                      </tr>
                    ))}
                    {teamBox.pitchingTotals && (
                      <tr className="tot">
                        <td className="nm">Totals</td>
                        <td>{teamBox.pitchingTotals.ip}</td><td>{teamBox.pitchingTotals.h}</td><td>{teamBox.pitchingTotals.r}</td>
                        <td>{teamBox.pitchingTotals.er}</td><td>{teamBox.pitchingTotals.bb}</td><td className="k">{teamBox.pitchingTotals.k}</td>
                        <td>{teamBox.pitchingTotals.hr}</td><td />
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="mgs-empty">No box data for this team.</div>
          )}
        </div>
      )}
      {info && info.length > 0 && (
        <div className="mgs-sec">
          <div className="mgs-sec-h">Game Info</div>
          <div className="mgs-ginfo">
            {info.map((n, i) => <div key={i} className="mgs-note"><b>{n.b}</b> {n.t}</div>)}
          </div>
        </div>
      )}
    </>
  );
}

// ---- Box/Table player-stats helpers (BUILD: matchup-daily "Player Stats") ----

const shortPlayerName = (name) => {
  if (!name) return '';
  const parts = String(name).trim().split(/\s+/);
  if (parts.length < 2) return name;
  return `${parts[0].charAt(0)}. ${parts.slice(1).join(' ')}`;
};

const formatHitterLine = (stat) => {
  if (!stat) return null;
  const parts = [];
  const push = (n, label) => { const v = n || 0; if (v > 0) parts.push(v > 1 ? `${v} ${label}` : label); };
  push(stat.hr, 'HR');
  push(stat.rbi, 'RBI');
  push(stat.r, 'R');
  push(stat.sb, 'SB');
  return { lead: `${stat.hits || 0}-${stat.atBats || 0}`, rest: parts.join(' · ') };
};

const formatPitcherLine = (stat) => {
  if (!stat) return null;
  const lead = (stat.w || 0) > 0 ? 'W' : (stat.sv || 0) > 0 ? 'SV' : '';
  return { lead, rest: `${stat.inningsPitched || 0} IP · ${stat.k || 0} K · ${stat.earnedRuns || 0} ER` };
};

// One team column of the "Box" player-stats view: compact player lines
// (pos pill · name · condensed stat line), hitters then pitchers.
function MbxTeamBox({ team, roster, stats, leagueId, onPlayerClick }) {
  const lineup = roster?.activeLineup || {};
  const outfielders = getOutfielders(lineup);
  const utility = getUtility(lineup);
  const pitchers = getPitchers(lineup);
  const hitterSlots = [
    ...['C', '1B', '2B', '3B', 'SS', 'MI', 'CI'].map(pos => ({ key: pos, pos, player: lineup[pos] || null })),
    ...Array.from({ length: 4 }, (_, i) => ({ key: `OF-${i}`, pos: 'OF', player: outfielders[i] || null })),
    ...Array.from({ length: 2 }, (_, i) => ({ key: `UT-${i}`, pos: 'UT', player: utility[i] || null })),
  ];
  const pitcherSlots = Array.from({ length: 9 }, (_, i) => ({ key: `P-${i}`, pos: 'P', player: pitchers[i] || null }));

  const renderRow = ({ key, pos, player }, isPitcher) => {
    const stat = player?.id ? stats?.[player.id] : null;
    const line = isPitcher ? formatPitcherLine(stat) : formatHitterLine(stat);
    const clickable = Boolean(onPlayerClick && player && leagueId);
    return (
      <div className="mbx-prow" key={key}>
        <span className={`pos-tag${isPitcher ? ' sp' : ''}`}>{pos}</span>
        {player ? (
          <span
            className={`mbx-pname${clickable ? ' clickable' : ''}`}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={clickable ? () => onPlayerClick(player, leagueId) : undefined}
            onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPlayerClick(player, leagueId); } } : undefined}
          >
            {shortPlayerName(player.name)}
          </span>
        ) : (
          <span className="mbx-pname mbx-dnp">Empty</span>
        )}
        {player && (line ? (
          <span className="mbx-pline">
            {line.lead && <b>{line.lead}</b>}
            {line.lead && line.rest ? ' · ' : ''}
            {line.rest}
          </span>
        ) : (
          <span className="mbx-pline mbx-dnp">No stats</span>
        ))}
      </div>
    );
  };

  return (
    <div className="mbx-col">
      <div className="mbx-col-head">
        <TeamLogo team={team} size={26} />
        <span className="mbx-ch-name">{team?.name || 'Team'}</span>
      </div>
      <div className="mbx-subhead">Hitters</div>
      {hitterSlots.map(slot => renderRow(slot, false))}
      <div className="mbx-subhead">Pitchers</div>
      {pitcherSlots.map(slot => renderRow(slot, true))}
    </div>
  );
}

// ---- Day strip / day-scoped sample data helpers (BUILD: matchup-daily .daystrip) ----

// Local YYYY-MM-DD (matches getCurrentDate()'s format for comparisons).
const toYMD = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// SAMPLE DATA — the FantasyData API is disabled app-wide (FANTASY_DATA_ENABLED
// = false), so per-day numbers cannot come from the network. The day strip maps
// data like this:
//   Total       -> the existing matchup ("full") aggregates, unchanged
//   current day -> the existing "today" aggregates, unchanged
//   other days  -> deterministic sample variation seeded from
//                  (team id, period, day index), using the same hash + sin
//                  seeding approach as LeaguePlayers.js (hashStr/seededRand)
//                  and LeagueMatchups.js, so values are stable across renders.
const hashSeed = (str) => {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (Math.imul(31, h) + str.charCodeAt(i)) | 0;
  return Math.abs(h);
};
const seededFrac = (seed) => {
  const x = Math.sin(seed + 1) * 10000;
  return x - Math.floor(x);
};
const sampleFrac = (...parts) => seededFrac(hashSeed(parts.join('|')));

// One sample single-day value for a category, derived from the team's full
// matchup value (counting stats scale to ~1/daysInPeriod of the total).
const sampleDayCategoryValue = (catKey, fullValue, teamId, week, dayIndex, daysInPeriod) => {
  const r = sampleFrac('day-cat', teamId, week, dayIndex, catKey);
  if (catKey === 'AVG') return (0.150 + r * 0.250).toFixed(3);
  if (catKey === 'ERA') return (1.50 + r * 4.00).toFixed(2);
  if (catKey === 'WHIP') return (0.80 + r * 0.80).toFixed(2);
  const perDay = (parseFloat(fullValue) || 0) / Math.max(daysInPeriod, 1);
  return Math.max(0, Math.round(perDay * (0.4 + 1.6 * r)));
};

// Build category results (values + per-category winner + win counts) for one
// sample day of a matchup. Same winner logic as the full/today results above.
const buildDayCategoryResults = (matchup, dayIndex, daysInPeriod) => {
  const categories = (matchup.fullCategoryResults || []).map((r) => r.category);
  let team1Wins = 0;
  let team2Wins = 0;
  const results = categories.map((cat) => {
    const team1Value = sampleDayCategoryValue(cat.key, matchup.team1FullStats?.[cat.key], matchup.team1.id, matchup.week, dayIndex, daysInPeriod);
    const team2Value = sampleDayCategoryValue(cat.key, matchup.team2FullStats?.[cat.key], matchup.team2.id, matchup.week, dayIndex, daysInPeriod);
    const v1 = parseFloat(team1Value);
    const v2 = parseFloat(team2Value);
    let winner = null;
    if (cat.higherWins ? v1 > v2 : v1 < v2) {
      winner = 'team1';
      team1Wins++;
    } else if (cat.higherWins ? v2 > v1 : v2 < v1) {
      winner = 'team2';
      team2Wins++;
    }
    return { category: cat, team1Value, team2Value, winner };
  });
  return { results, team1Wins, team2Wins };
};

// SAMPLE DATA — per-day variant of a team's player-stats map (see note above).
// Counting stats scale to a seeded ~single-day share of the loaded totals, so
// the Box/Table player views follow the selected day deterministically.
const PLAYER_DAY_COUNT_KEYS = ['r', 'hr', 'rbi', 'sb', 'w', 'sv', 'k', 'hits', 'atBats', 'inningsPitched', 'earnedRuns', 'hitsAllowed', 'walksAllowed'];
const deriveDayPlayerStats = (baseStats, teamId, week, dayIndex, daysInPeriod) => {
  const out = {};
  Object.keys(baseStats || {}).forEach((playerId) => {
    const s = baseStats[playerId];
    const derived = { ...s };
    PLAYER_DAY_COUNT_KEYS.forEach((key) => {
      const r = sampleFrac('day-player', teamId, week, dayIndex, playerId, key);
      const perDay = (Number(s?.[key]) || 0) / Math.max(daysInPeriod, 1);
      const scaled = perDay * (0.4 + 1.6 * r);
      derived[key] = key === 'inningsPitched' ? Math.round(scaled * 10) / 10 : Math.max(0, Math.round(scaled));
    });
    derived.avg = derived.atBats > 0 ? derived.hits / derived.atBats : 0;
    out[playerId] = derived;
  });
  return out;
};

// SAMPLE DATA — deterministic matchup-period (week) stat line per rostered
// player, keyed by player id. Hitters get a batting line, pitchers a pitching
// line. Used when the FantasyData API is disabled so the Live Matchup tables and
// day-strip render realistic numbers instead of dashes.
const smSeed = (id, key) => {
  const s = String(id) + key;
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  const x = Math.sin(Math.abs(h) + 1) * 10000;
  return x - Math.floor(x);
};
const buildSampleMatchupStats = (roster) => {
  const map = {};
  const players = [];
  const al = roster?.activeLineup || {};
  Object.values(al).forEach((v) => { if (Array.isArray(v)) players.push(...v); else if (v) players.push(v); });
  (roster?.players || []).forEach((p) => players.push(p));
  (roster?.bench || []).forEach((p) => players.push(p));
  players.filter(Boolean).forEach((p) => {
    if (p.id == null || map[p.id]) return;
    const isPitcher = ['P', 'SP', 'RP', 'RHP', 'LHP'].includes(p.position) || (p.positions || []).includes('P');
    if (isPitcher) {
      const inningsPitched = Math.round((3 + smSeed(p.id, 'ip') * 11) * 10) / 10;
      map[p.id] = {
        hits: 0, atBats: 0, r: 0, hr: 0, rbi: 0, sb: 0, avg: 0,
        w: Math.floor(smSeed(p.id, 'w') * 2),
        sv: smSeed(p.id, 'sv') > 0.8 ? Math.floor(smSeed(p.id, 'svn') * 3) + 1 : 0,
        k: Math.floor(inningsPitched * (0.8 + smSeed(p.id, 'k') * 0.5)),
        inningsPitched,
        earnedRuns: Math.floor(smSeed(p.id, 'er') * 5),
        hitsAllowed: Math.floor(smSeed(p.id, 'ha') * 11),
        walksAllowed: Math.floor(smSeed(p.id, 'wa') * 5),
      };
    } else {
      const atBats = Math.floor(15 + smSeed(p.id, 'ab') * 16);
      const hits = Math.floor(smSeed(p.id, 'h') * (atBats * 0.42));
      map[p.id] = {
        hits, atBats, avg: atBats > 0 ? hits / atBats : 0,
        r: Math.floor(smSeed(p.id, 'r') * 8),
        hr: Math.floor(smSeed(p.id, 'hr') * 4),
        rbi: Math.floor(smSeed(p.id, 'rbi') * 10),
        sb: Math.floor(smSeed(p.id, 'sb') * 4),
        w: 0, sv: 0, k: 0,
      };
    }
  });
  return map;
};

// Category keys counted as hitting for the totals-table group headers
// (everything else, plus GS, falls under the Pitching group).
const HITTING_CATEGORY_KEYS = new Set(['R', 'HR', 'RBI', 'SB', 'AVG', 'H', 'BB', 'TB', 'XBH', 'OBP', 'SLG', 'OPS']);

function LeagueScoreboard() {
  const { league, userTeam, selectedOpponent } = useOutletContext();
  // Global season-phase test-data mode (pre-draft / mid-season / after-season).
  const { mode: phase } = useTestDataBanner();
  const { openPlayerProfile } = usePlayerProfile();
  const [currentWeek, setCurrentWeek] = useState(1);
  const [allMatchups, setAllMatchups] = useState([]);
  const [selectedMatchupId, setSelectedMatchupId] = useState(null);
  // Day strip selection: 'total' = whole scoring period, or a 0-based index
  // into the selected period's days (see periodDays below).
  const [selectedDay, setSelectedDay] = useState('total');
  const [team1Roster, setTeam1Roster] = useState(null);
  const [team2Roster, setTeam2Roster] = useState(null);
  const [team1PlayerStats, setTeam1PlayerStats] = useState({});
  const [team2PlayerStats, setTeam2PlayerStats] = useState({});
  const [loadingPlayerStats, setLoadingPlayerStats] = useState(false);
  const [selectedGameId, setSelectedGameId] = useState(null);
  // MLB game sidebar: Live/Box tab + which team's box score is shown
  const [gameDetailTab, setGameDetailTab] = useState('live');
  const [boxTeamSide, setBoxTeamSide] = useState('home');
  // Player Stats section: full "Table" view by default, or compact "Box" columns
  const [playerStatsView, setPlayerStatsView] = useState('table');
  // Stat period next to the Box/Table toggle: Live (today's column, the default)
  // · Matchup (the whole scoring period) · Day (a specific day, chosen in the day
  // selector that appears in Day mode). Drives the effective `selectedDay` below.
  const [statPeriod, setStatPeriod] = useState('live');

  // MLB games for right rail - fetched from the shared liveGamesService (same
  // SportsData.io fetch/parsing logic used by the global GamesBanner ticker),
  // scoped to today's date. Falls back to sample data only if the fetch fails.
  const [mlbGames, setMlbGames] = useState(() => buildFallbackMlbGames(phase));

  // Rebuild the sample slate when the season-phase mode changes (live integration
  // is off, so the fetch effect below returns early and never refreshes these).
  useEffect(() => {
    if (!LIVE_GAMES_ENABLED) setMlbGames(buildFallbackMlbGames(phase));
  }, [phase]);

  useEffect(() => {
    // Live MLB integration disabled - keep the sample data already in state
    // and skip polling (see LIVE_GAMES_ENABLED in liveGamesService.js).
    if (!LIVE_GAMES_ENABLED) return undefined;

    let cancelled = false;
    const today = getCurrentDate();

    const loadTodaysMlbGames = async () => {
      try {
        const liveGames = await fetchGamesForDate(today);
        if (!cancelled) {
          setMlbGames(liveGames);
        }
      } catch (err) {
        console.error('Error loading MLB scores for rail, falling back to sample data:', err);
        if (!cancelled) {
          setMlbGames(buildFallbackMlbGames(phase));
        }
      }
    };

    loadTodaysMlbGames();
    const interval = setInterval(loadTodaysMlbGames, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  // Check season status
  const seasonStatus = useMemo(() => {
    // Season-phase test-data mode drives the season status directly: pre-draft =
    // not started (matchups 0-0), after-season = ended (final results).
    if (phase === 'pre-draft') return { status: 'not_started', currentPeriod: 1 };
    if (phase === 'after-season') {
      const storedMatchups = league?.settings?.schedule?.matchups || [];
      const lastPeriod = storedMatchups.length > 0 ? Math.max(...storedMatchups.map((m) => m.week || m.period || 1)) : 1;
      return { status: 'ended', currentPeriod: lastPeriod };
    }
    if (phase === 'mid-season') return { status: 'active', currentPeriod: 1 };

    const today = new Date(getCurrentDate() + 'T00:00:00');
    const seasonStart = league?.season?.startDate ? new Date(league.season.startDate + 'T00:00:00') : null;
    const seasonEnd = league?.season?.endDate ? new Date(league.season.endDate + 'T00:00:00') : null;
    
    if (!seasonStart) {
      return { status: 'not_started', currentPeriod: 1 };
    }
    
    if (today < seasonStart) {
      return { status: 'not_started', currentPeriod: 1 };
    }
    
    if (seasonEnd && today > seasonEnd) {
      // Season ended - show last period
      const storedMatchups = league?.settings?.schedule?.matchups || [];
      if (storedMatchups.length > 0) {
        const lastPeriod = Math.max(...storedMatchups.map(m => m.week || m.period || 1));
        return { status: 'ended', currentPeriod: lastPeriod };
      }
      return { status: 'ended', currentPeriod: 1 };
    }
    
    // Season is active - determine current period based on date
    // For now, default to period 1 if we can't determine
    return { status: 'active', currentPeriod: 1 };
  }, [phase, league?.season?.startDate, league?.season?.endDate, league?.settings?.schedule?.matchups]);

  const hasSeasonStarted = seasonStatus.status === 'active' || seasonStatus.status === 'ended';

  // Days of the selected scoring period (matchup-daily .daystrip). Periods can
  // span up to 14 days (bi-weekly / merged weeks); the strip scrolls if needed.
  const periodDays = useMemo(() => {
    const bounds = getMatchupDateBounds(currentWeek, league?.season?.startDate, league?.settings);
    if (!bounds) return [];
    const days = [];
    const cursor = new Date(bounds.start);
    let guard = 0; // safety cap: periods never exceed ~14 days
    while (cursor <= bounds.end && guard < 31) {
      days.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
      guard++;
    }
    return days;
  }, [currentWeek, league?.season?.startDate, league?.settings]);

  const todayYMD = getCurrentDate();
  const todayDayIndex = useMemo(
    () => periodDays.findIndex((d) => toYMD(d) === todayYMD),
    [periodDays, todayYMD]
  );
  const isTodaySelected = todayDayIndex >= 0 && selectedDay === todayDayIndex;

  // Resolve the effective day (selectedDay) the category + player-stat views read
  // from the stat-period selector. Live tracks today's column (falls back to the
  // period Total when today isn't inside this period); Matchup is the period
  // Total; Day keeps the day chosen in the selector, snapped to a valid day when
  // the period changes.
  useEffect(() => {
    if (statPeriod === 'matchup') {
      setSelectedDay('total');
    } else if (statPeriod === 'day') {
      setSelectedDay((prev) => (
        typeof prev === 'number' && prev < periodDays.length ? prev : (todayDayIndex >= 0 ? todayDayIndex : 0)
      ));
    } else {
      // live (default)
      setSelectedDay(todayDayIndex >= 0 ? todayDayIndex : 'total');
    }
  }, [statPeriod, currentWeek, todayDayIndex, periodDays.length]);

  // Check if a matchup is currently active
  const isMatchupActive = (matchup) => {
    const matchupWeek = matchup.week;
    
    // Matchup is active if it's the current week and status is 'Active'
    // For now, since season hasn't started, no matchups are active
    return false; // matchupWeek === currentWeek && matchup.status === 'Active';
  };

  // Load rosters for both teams in the selected matchup
  useEffect(() => {
    if (!selectedMatchupId || !allMatchups.length) {
      setTeam1Roster(null);
      setTeam2Roster(null);
      return;
    }

    const matchup = allMatchups.find(m => m.id === selectedMatchupId);
    if (!matchup) {
      setTeam1Roster(null);
      setTeam2Roster(null);
      return;
    }

    const loadRosters = async () => {
      const currentDate = getCurrentDate();
      const leagueId = league?.id || league?.leagueId;
      const [team1RosterData, team2RosterData] = await Promise.all([
        getRosterForDate(matchup.team1.owner, leagueId, currentDate, league),
        getRosterForDate(matchup.team2.owner, leagueId, currentDate, league)
      ]);
      setTeam1Roster(team1RosterData);
      setTeam2Roster(team2RosterData);
    };

    loadRosters();
  }, [selectedMatchupId, allMatchups, league]);

  // Load player stats for both teams
  useEffect(() => {
    if (!selectedMatchupId || !allMatchups.length || !team1Roster || !team2Roster) {
      setTeam1PlayerStats({});
      setTeam2PlayerStats({});
      return;
    }

    const matchup = allMatchups.find(m => m.id === selectedMatchupId);
    if (!matchup) {
      setTeam1PlayerStats({});
      setTeam2PlayerStats({});
      return;
    }

    const loadPlayerStats = async () => {
      setLoadingPlayerStats(true);
      try {
        // FantasyData API is disabled — populate deterministic sample matchup
        // stats for both rosters so the tables/day-strip render real numbers.
        const team1Stats = buildSampleMatchupStats(team1Roster);
        const team2Stats = buildSampleMatchupStats(team2Roster);

        // Set empty stats objects
        // if (shouldLoadTodayStats) {
        //   // Load today's stats
        //   const today = getCurrentDate();
        //   const year = new Date(today).getFullYear();
        //   const month = new Date(today).toLocaleString('en-US', { month: 'short' }).toUpperCase();
        //   const day = String(new Date(today).getDate()).padStart(2, '0');
        //   const formattedDate = `${year}-${month}-${day}`;
        //   
        //   try {
        //     const todayStats = await fantasyDataService.getPlayerGameStatsByDate(formattedDate);
        //     todayStats.forEach(stat => {
        //       const mapped = mapStatsFromAPI(stat);
        //       // Add hits and atBats for H/AB display
        //       mapped.hits = stat.Hits || 0;
        //       mapped.atBats = stat.AtBats || 0;
        //       
        //       if (team1PlayerIds.includes(stat.PlayerID)) {
        //         team1Stats[stat.PlayerID] = mapped;
        //       }
        //       if (team2PlayerIds.includes(stat.PlayerID)) {
        //         team2Stats[stat.PlayerID] = mapped;
        //       }
        //     });
        //   } catch (err) {
        //     console.warn('Error loading today\'s stats:', err);
        //   }
        // } else {
        //   // Load full matchup stats (week stats)
        //   // For now, use season stats as placeholder
        //   try {
        //     const seasonStats = await fantasyDataService.getPlayerSeasonStats('2026');
        //     seasonStats.forEach(stat => {
        //       const mapped = mapStatsFromAPI(stat);
        //       // Add hits and atBats for H/AB display
        //       mapped.hits = stat.Hits || 0;
        //       mapped.atBats = stat.AtBats || 0;
        //       
        //       if (team1PlayerIds.includes(stat.PlayerID)) {
        //         team1Stats[stat.PlayerID] = mapped;
        //       }
        //       if (team2PlayerIds.includes(stat.PlayerID)) {
        //         team2Stats[stat.PlayerID] = mapped;
        //       }
        //     });
        //   } catch (err) {
        //     console.warn('Error loading season stats:', err);
        //   }
        // }
        
        setTeam1PlayerStats(team1Stats);
        setTeam2PlayerStats(team2Stats);
      } catch (err) {
        console.error('Error loading player stats:', err);
      } finally {
        setLoadingPlayerStats(false);
      }
    };

    loadPlayerStats();
  }, [selectedMatchupId, allMatchups, team1Roster, team2Roster, isTodaySelected]);

  // Calculate overall records for teams (only if season has started)
  const calculateOverallRecords = () => {
    const records = {};
    const teams = Array.isArray(league.teams) ? league.teams : 
                  (typeof league.teams === 'string' ? JSON.parse(league.teams) : []);
    
    // Initialize records
    teams.forEach(team => {
      records[team.id] = { wins: 0, losses: 0, ties: 0 };
    });
    
    // Only calculate records if season has started
    if (!hasSeasonStarted) {
      return records; // Return empty records before season starts
    }
    
    // Calculate records from actual matchup results
    // For now, we'll use stored matchup data if available
    const storedMatchups = league?.settings?.schedule?.matchups || [];
    
    // Group matchups by period and calculate wins/losses
    storedMatchups.forEach(matchup => {
      const matchupPeriod = matchup.week || matchup.period || 1;
      
      // Only count matchups up to current period (or all if season ended)
      if (seasonStatus.status === 'ended' || matchupPeriod <= currentWeek) {
        // For now, we don't have actual results, so we'll leave records at 0-0-0
        // In a real implementation, you'd calculate from actual matchup results
      }
    });
    
    return records;
  };

  // Determine merged weeks based on settings
  const mergedWeeksInfo = useMemo(() => {
    const mergeFirstTwoWeeks = league?.settings?.schedule?.mergeFirstTwoWeeks !== false;
    const scoringType = league?.settings?.scoringType || '';
    const isHeadToHead = scoringType.includes('Head-to-Head');
    const seasonStartDate = league?.season?.startDate;
    
    const mergedWeeks = new Set();
    const weekMapping = {}; // Maps actual week number to display week number
    const displayWeekRanges = {}; // Maps display week to [startActualWeek, endActualWeek]
    
    // Get all-star break week if head-to-head
    let allStarWeek = null;
    if (isHeadToHead && seasonStartDate) {
      allStarWeek = getAllStarBreakWeek(seasonStartDate);
    }
    
    let displayWeek = 1;
    
    // Determine the maximum week to calculate based on stored matchups or default to 18
    const storedMatchups = league?.settings?.schedule?.matchups || [];
    const maxWeekFromMatchups = storedMatchups.length > 0
      ? Math.max(...storedMatchups.map(m => {
          // Get the actual week (before merging) if available, otherwise use display week
          return m.actualWeek || m.week || m.period || 1;
        }))
      : 18;
    
    // Also check playoff start week to ensure we include all regular season weeks
    const playoffStartWeek = league?.settings?.schedule?.playoffStartWeek;
    const maxWeek = playoffStartWeek ? Math.max(maxWeekFromMatchups, playoffStartWeek - 1) : maxWeekFromMatchups;
    
    // Calculate which weeks are merged and create mappings
    for (let actualWeek = 1; actualWeek <= maxWeek; actualWeek++) {
      // Handle merged first two weeks
      if (mergeFirstTwoWeeks && actualWeek === 1) {
        weekMapping[1] = displayWeek;
        weekMapping[2] = displayWeek; // Week 2 maps to same display week
        mergedWeeks.add(2);
        displayWeekRanges[displayWeek] = [1, 2];
        displayWeek++;
        continue;
      }
      if (mergeFirstTwoWeeks && actualWeek === 2) {
        continue; // Already handled above
      }
      
      // Handle all-star break week merged with following week
      if (isHeadToHead && allStarWeek && actualWeek === allStarWeek) {
        weekMapping[allStarWeek] = displayWeek;
        weekMapping[allStarWeek + 1] = displayWeek; // Next week maps to same display week
        mergedWeeks.add(allStarWeek);
        displayWeekRanges[displayWeek] = [allStarWeek, allStarWeek + 1];
        displayWeek++;
        continue;
      }
      if (isHeadToHead && allStarWeek && actualWeek === allStarWeek + 1) {
        continue; // Already handled above
      }
      
      // Regular week (not merged)
      weekMapping[actualWeek] = displayWeek;
      displayWeekRanges[displayWeek] = [actualWeek, actualWeek];
      displayWeek++;
    }
    
    return {
      mergedWeeks,
      weekMapping,
      displayWeekRanges,
      mergeFirstTwoWeeks,
      allStarWeek,
      isHeadToHead,
      totalDisplayWeeks: displayWeek - 1
    };
  }, [league?.settings?.schedule?.mergeFirstTwoWeeks, league?.settings?.scoringType, league?.season?.startDate, league?.settings?.schedule?.matchups, league?.settings?.schedule?.playoffStartWeek]);

  // Generate mock matchups with category stats for all weeks
  useEffect(() => {
    const generateAllMatchups = () => {
      const teams = Array.isArray(league.teams) ? league.teams : 
                    (typeof league.teams === 'string' ? JSON.parse(league.teams) : []);
      const allMatchupsList = [];
      const totalWeeks = 18;
      
      const { mergedWeeks, weekMapping } = mergedWeeksInfo;
      
      // Check if matchups are stored in league settings
      const storedMatchups = league?.settings?.schedule?.matchups || [];
      
      // Standard head-to-head categories
      const categories = [
        { key: 'R', label: 'R', higherWins: true },
        { key: 'HR', label: 'HR', higherWins: true },
        { key: 'RBI', label: 'RBI', higherWins: true },
        { key: 'SB', label: 'SB', higherWins: true },
        { key: 'AVG', label: 'AVG', higherWins: true },
        { key: 'W', label: 'W', higherWins: true },
        { key: 'SV', label: 'SV', higherWins: true },
        { key: 'K', label: 'K', higherWins: true },
        { key: 'ERA', label: 'ERA', higherWins: false },
        { key: 'WHIP', label: 'WHIP', higherWins: false },
      ];
      
      // Helper function to find team by ID
      const getTeamById = (teamId) => {
        return teams.find(t => t.id === teamId);
      };
      
      // If stored matchups exist, use them
      if (storedMatchups.length > 0) {
        storedMatchups.forEach(storedMatchup => {
          const team1 = getTeamById(storedMatchup.team1Id);
          const team2 = storedMatchup.team2Id ? getTeamById(storedMatchup.team2Id) : null;
          
          // Skip if teams not found or bye week
          if (!team1) return;
          if (!team2) return; // Skip bye weeks for now
          
          // Generate stats for this matchup
          const team1FullStats = {};
          const team2FullStats = {};
          const team1TodayStats = {};
          const team2TodayStats = {};
          
          // If season hasn't started, use empty stats
          if (!hasSeasonStarted) {
            categories.forEach(cat => {
              team1FullStats[cat.key] = cat.key === 'AVG' ? '0.000' : (cat.key === 'ERA' || cat.key === 'WHIP' ? '0.00' : 0);
              team2FullStats[cat.key] = cat.key === 'AVG' ? '0.000' : (cat.key === 'ERA' || cat.key === 'WHIP' ? '0.00' : 0);
              team1TodayStats[cat.key] = cat.key === 'AVG' ? '0.000' : (cat.key === 'ERA' || cat.key === 'WHIP' ? '0.00' : 0);
              team2TodayStats[cat.key] = cat.key === 'AVG' ? '0.000' : (cat.key === 'ERA' || cat.key === 'WHIP' ? '0.00' : 0);
            });
          } else {
            categories.forEach(cat => {
              if (cat.key === 'AVG') {
                team1FullStats[cat.key] = (Math.random() * 0.15 + 0.200).toFixed(3);
                team2FullStats[cat.key] = (Math.random() * 0.15 + 0.200).toFixed(3);
                team1TodayStats[cat.key] = (Math.random() * 0.05 + 0.200).toFixed(3);
                team2TodayStats[cat.key] = (Math.random() * 0.05 + 0.200).toFixed(3);
              } else if (cat.key === 'ERA') {
                team1FullStats[cat.key] = (Math.random() * 3 + 2.5).toFixed(2);
                team2FullStats[cat.key] = (Math.random() * 3 + 2.5).toFixed(2);
                team1TodayStats[cat.key] = (Math.random() * 1 + 3.0).toFixed(2);
                team2TodayStats[cat.key] = (Math.random() * 1 + 3.0).toFixed(2);
              } else if (cat.key === 'WHIP') {
                team1FullStats[cat.key] = (Math.random() * 0.6 + 1.0).toFixed(2);
                team2FullStats[cat.key] = (Math.random() * 0.6 + 1.0).toFixed(2);
                team1TodayStats[cat.key] = (Math.random() * 0.3 + 1.0).toFixed(2);
                team2TodayStats[cat.key] = (Math.random() * 0.3 + 1.0).toFixed(2);
              } else {
                const ranges = {
                  'R': [40, 80],
                  'HR': [8, 25],
                  'RBI': [35, 70],
                  'SB': [3, 15],
                  'W': [2, 8],
                  'SV': [0, 6],
                  'K': [50, 120]
                };
                const [min, max] = ranges[cat.key] || [0, 100];
                team1FullStats[cat.key] = Math.floor(Math.random() * (max - min) + min);
                team2FullStats[cat.key] = Math.floor(Math.random() * (max - min) + min);
                team1TodayStats[cat.key] = Math.floor((team1FullStats[cat.key] * 0.15) + Math.random() * 5);
                team2TodayStats[cat.key] = Math.floor((team2FullStats[cat.key] * 0.15) + Math.random() * 5);
              }
            });
          }
          
          // Calculate category wins
          let team1FullWins = 0;
          let team2FullWins = 0;
          const fullCategoryResults = categories.map(cat => {
            const team1Value = parseFloat(team1FullStats[cat.key]);
            const team2Value = parseFloat(team2FullStats[cat.key]);
            let winner = null;
            
            if (cat.higherWins) {
              if (team1Value > team2Value) {
                winner = 'team1';
                team1FullWins++;
              } else if (team2Value > team1Value) {
                winner = 'team2';
                team2FullWins++;
              }
            } else {
              if (team1Value < team2Value) {
                winner = 'team1';
                team1FullWins++;
              } else if (team2Value < team1Value) {
                winner = 'team2';
                team2FullWins++;
              }
            }
            
            return {
              category: cat,
              team1Value: team1FullStats[cat.key],
              team2Value: team2FullStats[cat.key],
              winner
            };
          });
          
          let team1TodayWins = 0;
          let team2TodayWins = 0;
          const todayCategoryResults = categories.map(cat => {
            const team1Value = parseFloat(team1TodayStats[cat.key]);
            const team2Value = parseFloat(team2TodayStats[cat.key]);
            let winner = null;
            
            if (cat.higherWins) {
              if (team1Value > team2Value) {
                winner = 'team1';
                team1TodayWins++;
              } else if (team2Value > team1Value) {
                winner = 'team2';
                team2TodayWins++;
              }
            } else {
              if (team1Value < team2Value) {
                winner = 'team1';
                team1TodayWins++;
              } else if (team2Value < team1Value) {
                winner = 'team2';
                team2TodayWins++;
              }
            }
            
            return {
              category: cat,
              team1Value: team1TodayStats[cat.key],
              team2Value: team2TodayStats[cat.key],
              winner
            };
          });
          
          const isActive = storedMatchup.week === currentWeek;
          const status = isActive ? 'Active' : (storedMatchup.week < currentWeek ? 'Final' : 'Scheduled');
          
          allMatchupsList.push({
            id: storedMatchup.id,
            week: storedMatchup.week,
            actualWeek: storedMatchup.actualWeek || storedMatchup.week,
            team1: team1,
            team2: team2,
            team1FullStats,
            team2FullStats,
            team1TodayStats,
            team2TodayStats,
            fullCategoryResults,
            todayCategoryResults,
            team1FullWins,
            team2FullWins,
            team1TodayWins,
            team2TodayWins,
            status
          });
        });
        
        setAllMatchups(allMatchupsList);
        return;
      }
      
      // Fallback: Generate matchups for all weeks (skip merged weeks)
      for (let week = 1; week <= totalWeeks; week++) {
        // Skip this week if it's merged
        if (mergedWeeks.has(week)) {
          continue;
        }
        
        const displayWeek = weekMapping[week] || week;
      
      // Simple round-robin pairing
      for (let i = 0; i < teams.length; i += 2) {
        if (i + 1 < teams.length) {
            // Generate full matchup stats
            const team1FullStats = {};
            const team2FullStats = {};
            
            // Generate today's stats (smaller values for active matchups)
            const team1TodayStats = {};
            const team2TodayStats = {};
            
            categories.forEach(cat => {
              if (cat.key === 'AVG') {
                team1FullStats[cat.key] = (Math.random() * 0.15 + 0.200).toFixed(3);
                team2FullStats[cat.key] = (Math.random() * 0.15 + 0.200).toFixed(3);
                team1TodayStats[cat.key] = (Math.random() * 0.05 + 0.200).toFixed(3);
                team2TodayStats[cat.key] = (Math.random() * 0.05 + 0.200).toFixed(3);
              } else if (cat.key === 'ERA') {
                team1FullStats[cat.key] = (Math.random() * 3 + 2.5).toFixed(2);
                team2FullStats[cat.key] = (Math.random() * 3 + 2.5).toFixed(2);
                team1TodayStats[cat.key] = (Math.random() * 1 + 3.0).toFixed(2);
                team2TodayStats[cat.key] = (Math.random() * 1 + 3.0).toFixed(2);
              } else if (cat.key === 'WHIP') {
                team1FullStats[cat.key] = (Math.random() * 0.6 + 1.0).toFixed(2);
                team2FullStats[cat.key] = (Math.random() * 0.6 + 1.0).toFixed(2);
                team1TodayStats[cat.key] = (Math.random() * 0.3 + 1.0).toFixed(2);
                team2TodayStats[cat.key] = (Math.random() * 0.3 + 1.0).toFixed(2);
              } else {
                const ranges = {
                  'R': [40, 80],
                  'HR': [8, 25],
                  'RBI': [35, 70],
                  'SB': [3, 15],
                  'W': [2, 8],
                  'SV': [0, 6],
                  'K': [50, 120]
                };
                const [min, max] = ranges[cat.key] || [0, 100];
                team1FullStats[cat.key] = Math.floor(Math.random() * (max - min) + min);
                team2FullStats[cat.key] = Math.floor(Math.random() * (max - min) + min);
                // Today's stats are a fraction of full stats
                team1TodayStats[cat.key] = Math.floor((team1FullStats[cat.key] * 0.15) + Math.random() * 5);
                team2TodayStats[cat.key] = Math.floor((team2FullStats[cat.key] * 0.15) + Math.random() * 5);
              }
            });
            
            // Calculate category wins for full matchup
            let team1FullWins = 0;
            let team2FullWins = 0;
            const fullCategoryResults = categories.map(cat => {
              const team1Value = parseFloat(team1FullStats[cat.key]);
              const team2Value = parseFloat(team2FullStats[cat.key]);
              let winner = null;
              
              if (cat.higherWins) {
                if (team1Value > team2Value) {
                  winner = 'team1';
                  team1FullWins++;
                } else if (team2Value > team1Value) {
                  winner = 'team2';
                  team2FullWins++;
                }
              } else {
                if (team1Value < team2Value) {
                  winner = 'team1';
                  team1FullWins++;
                } else if (team2Value < team1Value) {
                  winner = 'team2';
                  team2FullWins++;
                }
              }
              
              return {
                category: cat,
                team1Value: team1FullStats[cat.key],
                team2Value: team2FullStats[cat.key],
                winner
              };
            });
            
            // Calculate category wins for today's stats
            let team1TodayWins = 0;
            let team2TodayWins = 0;
            const todayCategoryResults = categories.map(cat => {
              const team1Value = parseFloat(team1TodayStats[cat.key]);
              const team2Value = parseFloat(team2TodayStats[cat.key]);
              let winner = null;
              
              if (cat.higherWins) {
                if (team1Value > team2Value) {
                  winner = 'team1';
                  team1TodayWins++;
                } else if (team2Value > team1Value) {
                  winner = 'team2';
                  team2TodayWins++;
                }
              } else {
                if (team1Value < team2Value) {
                  winner = 'team1';
                  team1TodayWins++;
                } else if (team2Value < team1Value) {
                  winner = 'team2';
                  team2TodayWins++;
                }
              }
              
              return {
                category: cat,
                team1Value: team1TodayStats[cat.key],
                team2Value: team2TodayStats[cat.key],
                winner
              };
            });
            
            // Determine status based on week
            const isActive = week === currentWeek;
            const status = isActive ? 'Active' : (week < currentWeek ? 'Final' : 'Scheduled');
            
            allMatchupsList.push({
              id: `week-${displayWeek}-matchup-${i/2}`,
              week: displayWeek, // Use display week number
              actualWeek: week, // Keep track of actual week for date calculations
            team1: teams[i],
            team2: teams[i + 1],
              team1FullStats,
              team2FullStats,
              team1TodayStats,
              team2TodayStats,
              fullCategoryResults,
              todayCategoryResults,
              team1FullWins,
              team2FullWins,
              team1TodayWins,
              team2TodayWins,
              status
            });
          }
        }
      }
      
      setAllMatchups(allMatchupsList);
      
      // Set default selected matchup to user's matchup in current week
      if (userTeam && allMatchupsList.length > 0) {
        const userMatchup = allMatchupsList.find(m => 
          (m.team1.id === userTeam.id || m.team2.id === userTeam.id) && 
          m.week === currentWeek
        );
        if (userMatchup) {
          setSelectedMatchupId(userMatchup.id);
        } else if (allMatchupsList.length > 0) {
          // Fallback to first matchup in current week
          const firstMatchup = allMatchupsList.find(m => m.week === currentWeek) || allMatchupsList[0];
          setSelectedMatchupId(firstMatchup.id);
        }
      } else if (allMatchupsList.length > 0) {
        const firstMatchup = allMatchupsList.find(m => m.week === currentWeek) || allMatchupsList[0];
        setSelectedMatchupId(firstMatchup.id);
      }
    };

    generateAllMatchups();
  }, [league.teams, currentWeek, userTeam, mergedWeeksInfo, league?.settings?.schedule?.matchups]);

  // Calculate total periods from stored matchups or use merged weeks info
  const storedMatchups = league?.settings?.schedule?.matchups || [];
  const maxPeriodFromMatchups = storedMatchups.length > 0 
    ? Math.max(...storedMatchups.map(m => m.week || m.period || 1))
    : 0;
  
  // Start with the larger of merged weeks display or actual matchups
  let totalPeriods = Math.max(mergedWeeksInfo.totalDisplayWeeks, maxPeriodFromMatchups);
  
  // If playoffs are enabled, calculate total including playoff periods
  const playoffStartPeriod = league?.settings?.schedule?.playoffStartWeek;
  if (playoffStartPeriod) {
    const playoffTeams = parseInt(league?.settings?.schedule?.playoffTeams || '4', 10);
    const playoffMatchupDuration = parseInt(league?.settings?.schedule?.playoffMatchupDuration || '1', 10);
    const numRounds = Math.ceil(Math.log2(playoffTeams));
    const totalPlayoffPeriods = numRounds * playoffMatchupDuration;
    
    // Total periods = regular season periods (up to playoff start) + playoff periods
    // Regular season ends at playoffStartPeriod - 1, then playoffs start
    const regularSeasonPeriods = playoffStartPeriod - 1;
    const totalWithPlayoffs = regularSeasonPeriods + totalPlayoffPeriods;
    
    totalPeriods = Math.max(totalPeriods, totalWithPlayoffs);
  }
  
  const totalWeeks = totalPeriods; // Use totalPeriods for the dropdown
  const overallRecords = calculateOverallRecords();
  
  // Set current week based on season status
  useEffect(() => {
    if (seasonStatus.currentPeriod !== currentWeek) {
      setCurrentWeek(seasonStatus.currentPeriod);
    }
  }, [seasonStatus.currentPeriod]);
  
  // Get selected matchup
  const selectedMatchup = allMatchups.find(m => m.id === selectedMatchupId);

  // Day-scoped category view for the selected matchup (day strip):
  //   Total        -> existing full-matchup aggregates, unchanged
  //   current day  -> existing "today" aggregates, unchanged
  //   other days   -> deterministic SAMPLE variation (buildDayCategoryResults)
  const isSampleDay = selectedDay !== 'total' && !isTodaySelected;
  const dayView = useMemo(() => {
    if (!selectedMatchup) return null;
    if (selectedDay === 'total' || !hasSeasonStarted) {
      return {
        categoryResults: selectedMatchup.fullCategoryResults,
        team1Wins: selectedMatchup.team1FullWins,
        team2Wins: selectedMatchup.team2FullWins
      };
    }
    if (selectedDay === todayDayIndex) {
      return {
        categoryResults: selectedMatchup.todayCategoryResults,
        team1Wins: selectedMatchup.team1TodayWins,
        team2Wins: selectedMatchup.team2TodayWins
      };
    }
    const { results, team1Wins, team2Wins } = buildDayCategoryResults(selectedMatchup, selectedDay, periodDays.length || 7);
    return { categoryResults: results, team1Wins, team2Wins };
  }, [selectedMatchup, selectedDay, todayDayIndex, hasSeasonStarted, periodDays.length]);

  const categoryResults = dayView?.categoryResults;
  const team1Wins = dayView?.team1Wins;
  const team2Wins = dayView?.team2Wins;

  // Day-scoped player stats for the Box/Table views: Total and the current day
  // use the loaded stats as-is; other days get deterministic sample variants.
  const team1DisplayStats = useMemo(() => (
    isSampleDay && selectedMatchup
      ? deriveDayPlayerStats(team1PlayerStats, selectedMatchup.team1.id, selectedMatchup.week, selectedDay, periodDays.length || 7)
      : team1PlayerStats
  ), [isSampleDay, selectedMatchup, team1PlayerStats, selectedDay, periodDays.length]);
  const team2DisplayStats = useMemo(() => (
    isSampleDay && selectedMatchup
      ? deriveDayPlayerStats(team2PlayerStats, selectedMatchup.team2.id, selectedMatchup.week, selectedDay, periodDays.length || 7)
      : team2PlayerStats
  ), [isSampleDay, selectedMatchup, team2PlayerStats, selectedDay, periodDays.length]);

  // Design-system player cell (rz-avatar + name + meta) for the H2H table.
  const rzMatchupPlayerCell = (player) => {
    if (!player) {
      return (
        <td className="rz-c-player">
          <div className="rz-player-cell">
            <span className="rz-avatar empty" />
            <div className="rz-pc-main"><div className="rz-pc-name rz-empty-name">Empty</div></div>
          </div>
        </td>
      );
    }
    const isPitcher = ['P', 'SP', 'RP', 'RHP', 'LHP'].includes(player.position) || (player.positions || []).includes('P');
    const photo = player.photoUrl || player.headshotUrl || player.image;
    const elig = (player.positions || [player.position]).filter((p) => p && p !== 'UTIL' && p !== 'P').slice(0, 3).join(', ');
    const open = () => openPlayerProfile(player, league?.id || league?.leagueId);
    return (
      <td className="rz-c-player">
        <div className="rz-player-cell">
          <button type="button" className={`rz-avatar ${isPitcher ? 'pitch' : 'hit'}`} onClick={open} title="Player profile" aria-label={`${player.name} profile`}>
            {photo ? <img src={photo} alt="" /> : <PlayerSilhouette />}
          </button>
          <div className="rz-pc-main">
            <div className="rz-pc-name"><button type="button" className="rz-name-btn" onClick={open}>{player.name}</button></div>
            <div className="rz-pc-meta">{player.team || 'FA'}{elig && <span className="rz-elig"> &middot; {elig}</span>}</div>
          </div>
        </div>
      </td>
    );
  };

  // Get active lineup positions in order (P is array, others are single player)
  const getLineupPositions = () => [
    'C', '1B', '2B', '3B', 'SS', 'MI', 'CI',
    'OF', 'UTIL', 'P'
  ];

  // Aggregate player stats for a team
  const aggregateTeamStats = (roster, playerStats) => {
    if (!roster || !roster.activeLineup) return {};

    const aggregated = {
      R: 0, HR: 0, RBI: 0, SB: 0, AVG: 0,
      W: 0, SV: 0, K: 0, ERA: 0, WHIP: 0,
      atBats: 0, hits: 0, inningsPitched: 0, earnedRuns: 0, hitsAllowed: 0, walksAllowed: 0
    };

    const lineupPositions = getLineupPositions();
    lineupPositions.forEach(position => {
      const players = position === 'P' ? getPitchers(roster.activeLineup)
        : position === 'OF' ? getOutfielders(roster.activeLineup)
        : position === 'UTIL' ? getUtility(roster.activeLineup)
        : [roster.activeLineup[position]].filter(Boolean);
      players.forEach(player => {
        if (player && player.id && playerStats[player.id]) {
          const stats = playerStats[player.id];
          aggregated.R += stats.r || 0;
          aggregated.HR += stats.hr || 0;
          aggregated.RBI += stats.rbi || 0;
          aggregated.SB += stats.sb || 0;
          aggregated.W += stats.w || 0;
          aggregated.SV += stats.sv || 0;
          aggregated.K += stats.k || 0;
          aggregated.atBats += stats.atBats || 0;
          aggregated.hits += stats.hits || 0;
          aggregated.inningsPitched += stats.inningsPitched || 0;
          aggregated.earnedRuns += stats.earnedRuns || 0;
          aggregated.hitsAllowed += stats.hitsAllowed || 0;
          aggregated.walksAllowed += stats.walksAllowed || 0;
        }
      });
    });
    
    // Calculate AVG
    aggregated.AVG = aggregated.atBats > 0 ? (aggregated.hits / aggregated.atBats).toFixed(3) : '0.000';
    
    // Calculate ERA
    aggregated.ERA = aggregated.inningsPitched > 0 
      ? ((aggregated.earnedRuns * 9) / aggregated.inningsPitched).toFixed(2)
      : '0.00';
    
    // Calculate WHIP
    aggregated.WHIP = aggregated.inningsPitched > 0
      ? ((aggregated.hitsAllowed + aggregated.walksAllowed) / aggregated.inningsPitched).toFixed(2)
      : '0.00';
    
    return aggregated;
  };

  // Get matchups for the current week for sidebar
  const weekMatchups = allMatchups.filter(m => m.week === currentWeek);
  
  // Calculate matchup dates for each week (handles merged weeks)
  // Use the imported getMatchupDateRange function which handles merged weeks
  const getMatchupDateRangeLocal = (displayWeekNumber) => {
    return getMatchupDateRange(displayWeekNumber, league?.season?.startDate, league?.settings);
  };

  const selectedGame = mlbGames.find(g => g.GameID === selectedGameId);
  const liveGamesCount = mlbGames.filter(isGameLive).length;
  const openGameDetail = (gameId) => {
    setSelectedGameId(gameId);
    setGameDetailTab('live');
    setBoxTeamSide('home');
  };

  // Change the matchup period (used by the design-system period select and the
  // prev/next steppers). Bounds to 1..totalWeeks and follows the user's own
  // matchup for the new period when there is one.
  const changeMatchupWeek = (newWeek) => {
    if (Number.isNaN(newWeek) || newWeek < 1 || newWeek > totalWeeks) return;
    setCurrentWeek(newWeek);
    const userMatchup = userTeam && allMatchups.find(m => (m.team1.id === userTeam.id || m.team2.id === userTeam.id) && m.week === newWeek);
    const pick = userMatchup || allMatchups.find(m => m.week === newWeek);
    if (pick) setSelectedMatchupId(pick.id);
  };

  return (
    <div style={{ paddingBottom: '24px' }}>
      <div className="live-matchup-grid">
        {/* Left rail: matchup period selector + the week's matchups (vertical list) */}
        <div className="live-matchup-list-rail">
          {/* Matchup period selector (design-system period control,
              matching the roster's rz-period stepper) */}
          <div className="lm-rail-period">
            <div className="rz-period">
              <button type="button" className="rz-pstep" onClick={() => changeMatchupWeek(currentWeek - 1)} disabled={currentWeek <= 1} title="Previous period" aria-label="Previous period">&#8249;</button>
              <div className="rz-pselect">
                <select
                  id="matchup-selector"
                  value={currentWeek}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (!value || value === '') return;
                    changeMatchupWeek(parseInt(value));
                  }}
                  aria-label="Matchup period"
                >
          {(() => {
            const options = [];
            const playoffStartPeriod = league?.settings?.schedule?.playoffStartWeek;
            const regularSeasonPeriods = playoffStartPeriod ? playoffStartPeriod - 1 : totalWeeks;
            for (let i = 1; i <= regularSeasonPeriods; i++) {
              options.push(<option key={i} value={i}>{i} ({getMatchupDateRangeLocal(i)})</option>);
            }
            if (playoffStartPeriod && totalWeeks > regularSeasonPeriods) {
              options.push(<option key="playoff-separator" value="" disabled style={{ fontWeight: 'bold', backgroundColor: 'var(--surface-muted)' }}>-------- Playoffs --------</option>);
              for (let i = playoffStartPeriod; i <= totalWeeks; i++) {
                options.push(<option key={i} value={i}>{i} ({getMatchupDateRangeLocal(i)})</option>);
              }
            } else if (!playoffStartPeriod) {
              for (let i = regularSeasonPeriods + 1; i <= totalWeeks; i++) {
                options.push(<option key={i} value={i}>{i} ({getMatchupDateRangeLocal(i)})</option>);
              }
            }
            return options;
          })()}
                </select>
                <span className="rz-pcaret">&#9662;</span>
              </div>
              <button type="button" className="rz-pstep" onClick={() => changeMatchupWeek(currentWeek + 1)} disabled={currentWeek >= totalWeeks} title="Next period" aria-label="Next period">&#8250;</button>
            </div>
          </div>
          <div className="lm-matchup-list">
            {/* Matchups for the selected period, stacked as a vertical list.
                Micro score stacked cards (design-system/micro-scoreboard.html),
                same classes/markup as LeagueHome's Live Matchup section. */}
            {weekMatchups.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: 'var(--muted)', fontSize: '14px' }}>
                No matchups for this week
              </div>
            ) : (
              weekMatchups.map((matchup) => {
                const isSelected = selectedMatchupId === matchup.id;
                const matchupTeam1Wins = hasSeasonStarted ? (matchup.team1FullWins || 0) : 0;
                const matchupTeam2Wins = hasSeasonStarted ? (matchup.team2FullWins || 0) : 0;
                const leagueSettings = typeof league?.settings === 'string' ? JSON.parse(league.settings || '{}') : (league?.settings || {});
                const scoringCategories = leagueSettings.scoring?.categories || [];
                const totalCategories = scoringCategories.length || 10;
                const matchupTies = totalCategories - matchupTeam1Wins - matchupTeam2Wins;
                const isLive = matchup.status === 'Active';
                return (
                  <div
                    key={matchup.id}
                    className={`micro-card${isSelected ? ' selected' : ''}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedMatchupId(matchup.id)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedMatchupId(matchup.id); } }}
                  >
                    <div className={`micro-card-row${matchupTeam1Wins > matchupTeam2Wins ? ' win' : ''}`}>
                      <TeamLogo team={matchup.team1} size={24} style={{ borderRadius: '6px' }} />
                      <span className="micro-card-name">{matchup.team1.name}</span>
                      <span className="micro-card-score">{matchupTeam1Wins}</span>
                    </div>
                    <div className="micro-card-div" />
                    <div className={`micro-card-row${matchupTeam2Wins > matchupTeam1Wins ? ' win' : ''}`}>
                      <TeamLogo team={matchup.team2} size={24} style={{ borderRadius: '6px' }} />
                      <span className="micro-card-name">{matchup.team2.name}</span>
                      <span className="micro-card-score">{matchupTeam2Wins}</span>
                    </div>
                    <div className="micro-card-foot">
                      <span className="micro-card-status">
                        {isLive ? (
                          <>
                            <span className="micro-card-dot" /> Live
                          </>
                        ) : (
                          matchup.status === 'Final' ? 'Final' : 'Scheduled'
                        )}
                      </span>
                      {matchupTies > 0 && (
                        <span className="micro-card-cat">
                          {matchupTies} {matchupTies === 1 ? 'Tie' : 'Ties'}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

      {/* Center column: selected matchup detail */}
      <div className="live-matchup-detail" style={{ minWidth: 0 }}>
        
        {!selectedMatchup ? (
          <div style={{ textAlign: 'center', padding: '30px', color: 'var(--muted)' }}>
            <h4>No matchup selected</h4>
            <p>Please select a matchup to view.</p>
          </div>
        ) : (
          <div style={{ marginTop: '10px' }}>
            {(() => {
              const isMyTeam1 = userTeam && selectedMatchup.team1.id === userTeam.id;
              const isMyTeam2 = userTeam && selectedMatchup.team2.id === userTeam.id;
              const isMyMatchup = isMyTeam1 || isMyTeam2;
              const team1Winning = team1Wins > team2Wins;
              const team2Winning = team2Wins > team1Wins;
              
              const team1Record = overallRecords[selectedMatchup.team1.id] || { wins: 0, losses: 0, ties: 0 };
              const team2Record = overallRecords[selectedMatchup.team2.id] || { wins: 0, losses: 0, ties: 0 };
              
              // Get matchup limits from league settings
              const leagueSettings = typeof league?.settings === 'string' 
                ? JSON.parse(league.settings || '{}') 
                : (league?.settings || {});
              const matchupLimits = leagueSettings.matchupLimits || {};
              const gamesStartedEnabled = matchupLimits.gamesStartedEnabled || false;
              const maxGamesStarted = matchupLimits.maxGamesStarted || 0;
              
              // Calculate games started for each team (placeholder - in production this would come from matchup data)
              // For now, using 0 as placeholder. In production, this should count pitchers who started games
              const team1GamesStarted = selectedMatchup.team1Stats?.gamesStarted || 0;
              const team2GamesStarted = selectedMatchup.team2Stats?.gamesStarted || 0;

              // Category totals table (matchup-daily .dt): split categories into
              // Hitting / Pitching groups; GS stays appended to the pitching group.
              const hittingResults = (categoryResults || []).filter(r => HITTING_CATEGORY_KEYS.has(r.category.key));
              const pitchingResults = (categoryResults || []).filter(r => !HITTING_CATEGORY_KEYS.has(r.category.key));

              const zeroFillValue = (catKey) => (
                catKey === 'AVG' ? '0.000' : (catKey === 'ERA' || catKey === 'WHIP' ? '0.00' : '0')
              );

              const renderDayTotalsRow = (sideKey, team, gamesStarted) => {
                const gsExceeded = gamesStartedEnabled && gamesStarted > maxGamesStarted;
                return (
                  <tr>
                    <td className="mdd-team-c">
                      <span className="mdd-team-wrap">
                        <TeamLogo team={team} size={22} style={{ borderRadius: '6px' }} />
                        <span className="mdd-tnm">{team?.name}</span>
                      </span>
                    </td>
                    {hittingResults.map((result) => {
                      const value = sideKey === 'team1' ? result.team1Value : result.team2Value;
                      return (
                        <td key={result.category.key} className={result.winner === sideKey ? 'mdd-lead' : undefined}>
                          {hasSeasonStarted ? value : zeroFillValue(result.category.key)}
                        </td>
                      );
                    })}
                    {pitchingResults.map((result, i) => {
                      const value = sideKey === 'team1' ? result.team1Value : result.team2Value;
                      const classes = [i === 0 ? 'mdd-col-div' : '', result.winner === sideKey ? 'mdd-lead' : ''].filter(Boolean).join(' ');
                      return (
                        <td key={result.category.key} className={classes || undefined}>
                          {hasSeasonStarted ? value : zeroFillValue(result.category.key)}
                        </td>
                      );
                    })}
                    <td style={gsExceeded ? { color: 'var(--danger)' } : undefined}>
                      {gamesStartedEnabled && maxGamesStarted > 0 ? `${gamesStarted}/${maxGamesStarted}` : '0/12'}
                    </td>
                  </tr>
                );
              };

              return (
              <div>
                {/* Status Info */}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '15px',
                  paddingBottom: '8px',
                  borderBottom: '1px solid var(--border)'
                }}>
                  <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                    Period {selectedMatchup.week} • Status: <strong>{selectedMatchup.status}</strong>
                  </div>
                </div>
                
                {/* Category totals table (matchup-daily .dt): Hitting/Pitching
                    group headers, team cells with logo + name, leading value
                    per category in navy bold. W-L-T cats summary sits in the
                    top-left header cell. */}
                <div style={{ marginBottom: '15px' }}>
                  <div style={{ fontSize: '12px', color: 'var(--muted)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                    Score · Stats · Limits
                  </div>
                  <div className="mdd-daytotals">
                    <div className="mdd-dt-scroll">
                      <table className="mdd-dt">
                        <thead>
                          <tr>
                            <th className="mdd-rec-h" rowSpan={2}>
                              <span className="mdd-rec">
                                {!hasSeasonStarted || !categoryResults ? '0-0-10' : `${team1Wins}-${team2Wins}-${(categoryResults.length - team1Wins - team2Wins)}`}
                              </span>
                            </th>
                            {hittingResults.length > 0 && (
                              <th className="mdd-gh mdd-gh-hit" colSpan={hittingResults.length}>Hitting</th>
                            )}
                            <th className="mdd-gh mdd-gh-pit mdd-col-div" colSpan={pitchingResults.length + 1}>Pitching</th>
                          </tr>
                          <tr>
                            {hittingResults.map((r) => (
                              <th key={r.category.key}>{r.category.label}</th>
                            ))}
                            {pitchingResults.map((r, i) => (
                              <th key={r.category.key} className={i === 0 ? 'mdd-col-div' : undefined}>{r.category.label}</th>
                            ))}
                            <th>GS</th>
                          </tr>
                        </thead>
                        <tbody>
                          {renderDayTotalsRow('team1', selectedMatchup.team1, team1GamesStarted)}
                          {renderDayTotalsRow('team2', selectedMatchup.team2, team2GamesStarted)}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Player Stats: stat-period (Live/Matchup/Day) + Box|Table view
                    toggles. Day mode reveals the day selector below. */}
                <div className="mbx-viewbar">
                  <span className="mbx-vb-label">Player Stats</span>
                  <div className="mbx-vb-controls">
                    <div className="mbx-vtoggle">
                      <button type="button" className={statPeriod === 'live' ? 'on' : undefined} onClick={() => setStatPeriod('live')}>Live</button>
                      <button type="button" className={statPeriod === 'matchup' ? 'on' : undefined} onClick={() => setStatPeriod('matchup')}>Matchup</button>
                      <button type="button" className={statPeriod === 'day' ? 'on' : undefined} onClick={() => setStatPeriod('day')}>Day</button>
                    </div>
                    <div className="mbx-vtoggle">
                      <button type="button" className={playerStatsView === 'box' ? 'on' : undefined} onClick={() => setPlayerStatsView('box')}>Box</button>
                      <button type="button" className={playerStatsView === 'table' ? 'on' : undefined} onClick={() => setPlayerStatsView('table')}>Table</button>
                    </div>
                  </div>
                </div>

                {/* Day mode: pick a specific day of the scoring period. Live and
                    Matchup don't need the strip (Live = today, Matchup = Total). */}
                {statPeriod === 'day' && (
                  <div className="mdd-daystrip mdd-daystrip-inline">
                    {periodDays.map((day, i) => {
                      const dayYMD = toYMD(day);
                      const isSel = selectedDay === i;
                      const isDone = dayYMD < todayYMD;
                      return (
                        <div
                          key={dayYMD}
                          role="button"
                          tabIndex={0}
                          className={`mdd-day${isSel ? ' sel' : ''}${isDone ? ' done' : ''}`}
                          onClick={() => setSelectedDay(i)}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedDay(i); } }}
                        >
                          <span className="mdd-dow">{day.toLocaleDateString('en-US', { weekday: 'short' })}</span>
                          <span className="mdd-dnum">{day.getDate()}</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Box view: two team columns of compact player lines */}
                {playerStatsView === 'box' && (
                  <div className="mbx-cols">
                    <MbxTeamBox
                      team={selectedMatchup.team1}
                      roster={team1Roster}
                      stats={team1DisplayStats}
                      leagueId={league?.id || league?.leagueId}
                      onPlayerClick={openPlayerProfile}
                    />
                    <MbxTeamBox
                      team={selectedMatchup.team2}
                      roster={team2Roster}
                      stats={team2DisplayStats}
                      leagueId={league?.id || league?.leagueId}
                      onPlayerClick={openPlayerProfile}
                    />
                  </div>
                )}

                {/* Table view: head-to-head player table (design-system rz table) */}
                {playerStatsView === 'table' && (
                <div className="rz-card">
                  <div className="rz-tbl-wrap rz-tbl-wrap-players">
                  <table className="rz-tbl rz-tbl-players" style={{ fontSize: '12px' }}>
                    <thead>
                      <tr>
                        {/* Team 1 headers */}
                        <th className="rz-c-player" style={{ textAlign: 'left' }}>Player</th>
                        <th style={{ textAlign: 'left' }}>Game</th>
                        <th className="rz-c-stat rz-num">H/AB</th>
                        <th className="rz-c-stat rz-num">R</th>
                        <th className="rz-c-stat rz-num">HR</th>
                        <th className="rz-c-stat rz-num">RBI</th>
                        <th className="rz-c-stat rz-num">SB</th>
                        <th className="rz-c-stat rz-num">AVG</th>
                        {/* Center position divider */}
                        <th className="msb-h2h-pos">Pos</th>
                        {/* Team 2 headers */}
                        <th className="rz-c-player" style={{ textAlign: 'left' }}>Player</th>
                        <th style={{ textAlign: 'left' }}>Game</th>
                        <th className="rz-c-stat rz-num">H/AB</th>
                        <th className="rz-c-stat rz-num">R</th>
                        <th className="rz-c-stat rz-num">HR</th>
                        <th className="rz-c-stat rz-num">RBI</th>
                        <th className="rz-c-stat rz-num">SB</th>
                        <th className="rz-c-stat rz-num">AVG</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getLineupPositions().flatMap((position) => {
                        if (position === 'P') {
                          const p1 = getPitchers(team1Roster?.activeLineup || {});
                          const p2 = getPitchers(team2Roster?.activeLineup || {});
                          return Array.from({ length: 9 }, (_, i) => ({
                            position: 'P', team1Player: p1[i] || null, team2Player: p2[i] || null, key: `P-${i}`
                          }));
                        }
                        if (position === 'OF') {
                          const p1 = getOutfielders(team1Roster?.activeLineup || {});
                          const p2 = getOutfielders(team2Roster?.activeLineup || {});
                          return Array.from({ length: 4 }, (_, i) => ({
                            position: 'OF', team1Player: p1[i] || null, team2Player: p2[i] || null, key: `OF-${i}`
                          }));
                        }
                        if (position === 'UTIL') {
                          const p1 = getUtility(team1Roster?.activeLineup || {});
                          const p2 = getUtility(team2Roster?.activeLineup || {});
                          return Array.from({ length: 2 }, (_, i) => ({
                            position: 'UT', team1Player: p1[i] || null, team2Player: p2[i] || null, key: `UTIL-${i}`
                          }));
                        }
                        return [{
                          position, team1Player: team1Roster?.activeLineup?.[position], team2Player: team2Roster?.activeLineup?.[position], key: position
                        }];
                      }).map(({ position, team1Player, team2Player, key }, idx) => {
                        const team1PlayerStat = team1Player?.id ? team1DisplayStats[team1Player.id] : null;
                        const team2PlayerStat = team2Player?.id ? team2DisplayStats[team2Player.id] : null;

                        return (
                          <tr
                            key={key}
                          >
                            {/* Team 1 player */}
                            {rzMatchupPlayerCell(team1Player)}
                            <td className="pgs-cell">
                              {team1Player ? <PlayerGameStatus status={getPlayerGameStatus(team1Player, mlbGames)} /> : '—'}
                            </td>
                            <td className="rz-c-stat rz-num">{team1PlayerStat && team1PlayerStat.atBats > 0 ? `${team1PlayerStat.hits || 0}/${team1PlayerStat.atBats}` : '—'}</td>
                            <td className="rz-c-stat rz-num">{team1PlayerStat?.r || '—'}</td>
                            <td className="rz-c-stat rz-num">{team1PlayerStat?.hr || '—'}</td>
                            <td className="rz-c-stat rz-num">{team1PlayerStat?.rbi || '—'}</td>
                            <td className="rz-c-stat rz-num">{team1PlayerStat?.sb || '—'}</td>
                            <td className="rz-c-stat rz-num">{team1PlayerStat?.avg ? (typeof team1PlayerStat.avg === 'number' ? team1PlayerStat.avg.toFixed(3) : parseFloat(team1PlayerStat.avg).toFixed(3)) : '—'}</td>
                            {/* Center position divider */}
                            <td className="msb-h2h-pos">{position.replace(/\d+$/, '')}</td>
                            {/* Team 2 player */}
                            {rzMatchupPlayerCell(team2Player)}
                            <td className="pgs-cell">
                              {team2Player ? <PlayerGameStatus status={getPlayerGameStatus(team2Player, mlbGames)} /> : '—'}
                            </td>
                            <td className="rz-c-stat rz-num">{team2PlayerStat && team2PlayerStat.atBats > 0 ? `${team2PlayerStat.hits || 0}/${team2PlayerStat.atBats}` : '—'}</td>
                            <td className="rz-c-stat rz-num">{team2PlayerStat?.r || '—'}</td>
                            <td className="rz-c-stat rz-num">{team2PlayerStat?.hr || '—'}</td>
                            <td className="rz-c-stat rz-num">{team2PlayerStat?.rbi || '—'}</td>
                            <td className="rz-c-stat rz-num">{team2PlayerStat?.sb || '—'}</td>
                            <td className="rz-c-stat rz-num">{team2PlayerStat?.avg ? (typeof team2PlayerStat.avg === 'number' ? team2PlayerStat.avg.toFixed(3) : parseFloat(team2PlayerStat.avg).toFixed(3)) : '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>
                </div>
                )}
              </div>
              );
            })()}
          </div>
        )}
      </div>
        {/* Right rail: MLB game sidebar (design-system/live-sidebar.html) */}
        <div className="live-matchup-mlb-rail mgs-sidebar">
          {!selectedGame ? (
            <>
              {/* All-games list state */}
              <div className="mgs-head">
                <div>
                  <div className="mgs-title">MLB Scores</div>
                  <div className="mgs-date">{new Date(getCurrentDate() + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</div>
                </div>
                {liveGamesCount > 0 && (
                  <span className="mgs-count"><span className="mgs-dot" /> {liveGamesCount} Live</span>
                )}
              </div>
              <div className="mgs-list">
              {mlbGames.map((game) => {
                const live = isGameLive(game);
                const final = isGameFinal(game);
                const started = live || final;
                const away = game.AwayTeamRuns ?? 0;
                const home = game.HomeTeamRuns ?? 0;
                return (
                  <div className="mgs-game" key={game.GameID}>
                    <div
                      className="mgs-grow"
                      role="button"
                      tabIndex={0}
                      onClick={() => openGameDetail(game.GameID)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openGameDetail(game.GameID); } }}
                    >
                      <div className="mgs-state">
                        {live ? (
                          <>
                            <div className="mgs-inn">{game.InningHalf === 'B' ? '▼' : '▲'} {game.Inning}</div>
                            <div className="mgs-live">Live</div>
                          </>
                        ) : (
                          <div className="mgs-final">{final ? 'Final' : formatGameTime(game.DateTime)}</div>
                        )}
                      </div>
                      <div className="mgs-teams">
                        <div className={`mgs-tline${started && away > home ? ' lead' : ''}`}>
                          <span className="mgs-abbr">{game.AwayTeam}</span>
                          <span className="mgs-sc">{started ? away : '–'}</span>
                        </div>
                        <div className={`mgs-tline${started && home > away ? ' lead' : ''}`}>
                          <span className="mgs-abbr">{game.HomeTeam}</span>
                          <span className="mgs-sc">{started ? home : '–'}</span>
                        </div>
                      </div>
                      <div className="mgs-sit">
                        {live && (
                          <>
                            <MgsBases game={game} />
                            <MgsOuts outs={game.Outs} />
                          </>
                        )}
                      </div>
                      <span className="mgs-chev">›</span>
                    </div>
                  </div>
                );
              })}
              </div>
            </>
          ) : (
            <>
              {/* Opened-game state: back bar, score header, Live/Box tabs */}
              <div
                className="mgs-back"
                role="button"
                tabIndex={0}
                onClick={() => setSelectedGameId(null)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedGameId(null); } }}
              >
                <span className="mgs-arr">‹</span> All Games
              </div>
              {(() => {
                const live = isGameLive(selectedGame);
                const final = isGameFinal(selectedGame);
                const started = live || final;
                const away = selectedGame.AwayTeamRuns ?? 0;
                const home = selectedGame.HomeTeamRuns ?? 0;
                return (
                  <div className="mgs-ghx">
                    <div className={`mgs-ghx-team${started && away < home ? ' lose' : ''}`}>
                      <span className="mgs-ghx-abbr">{selectedGame.AwayTeam}</span>
                      <span className="mgs-ghx-score">{started ? away : '–'}</span>
                    </div>
                    <div className="mgs-ghx-mid">
                      {live ? (
                        <>
                          <div className="mgs-ghx-inn">{selectedGame.InningHalf === 'B' ? '▼' : '▲'} {inningOrdinal(selectedGame.Inning)}</div>
                          <MgsBases game={selectedGame} />
                          <MgsOuts outs={selectedGame.Outs} />
                        </>
                      ) : (
                        <div className="mgs-ghx-inn">{final ? 'Final' : formatGameTime(selectedGame.DateTime)}</div>
                      )}
                    </div>
                    <div className={`mgs-ghx-team right${started && home < away ? ' lose' : ''}`}>
                      <span className="mgs-ghx-abbr">{selectedGame.HomeTeam}</span>
                      <span className="mgs-ghx-score">{started ? home : '–'}</span>
                    </div>
                  </div>
                );
              })()}
              <div className="mgs-tabs">
                <button type="button" className={`mgs-tab${gameDetailTab === 'live' ? ' on' : ''}`} onClick={() => setGameDetailTab('live')}>Live</button>
                <button type="button" className={`mgs-tab${gameDetailTab === 'box' ? ' on' : ''}`} onClick={() => setGameDetailTab('box')}>Box</button>
              </div>
              <div className="mgs-gbody">
                {gameDetailTab === 'live'
                  ? <MgsLiveTab game={selectedGame} />
                  : <MgsBoxTab game={selectedGame} side={boxTeamSide} onSideChange={setBoxTeamSide} />}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default LeagueScoreboard;

